import * as React from 'react';
import { authenticatedFetcher } from '@/lib/swr';
import { cn } from '@/lib/utils';
import { PP_BASE_URL } from '@/lib/env';
import type { AddressValue, Area, Region, Zone } from './addressTypes';
import { getId, getTitleEn, getTitleAr, getManhalCode, isDubaiOrAbuDhabi } from './addressTypes';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DeferredInput } from '@/components/ui/deferredInput';

// ============================================
// Dubai & Northern Emirates Layout
// ============================================
type DubaiNorthernEmiratesFieldsProps = {
  local: AddressValue;
  emit: (updates: Partial<AddressValue>) => void;
  disabled?: boolean;
  touched: { [K in keyof AddressValue]?: boolean };
  setTouched: React.Dispatch<React.SetStateAction<{ [K in keyof AddressValue]?: boolean }>>;
  regions: Region[];
  zones: Zone[];
  areas: Area[];
  regionsLoading: boolean;
  zonesLoading: boolean;
  areasLoading: boolean;
  emiratesLoading: boolean;
  regionError: boolean;
  zoneError: boolean;
  areaError: boolean;
  l: {
    region: string;
    zone: string;
    area: string;
    requiredField: string;
  };
  req: {
    region: boolean;
    zone: boolean;
    area: boolean;
  };
  isRTL: boolean;
  locale: string;
  t: {
    pickLocation: {
      loading: string;
      noRegions: string;
      noZones: string;
      noAreas: string;
    };
  };
};

export function DubaiNorthernEmiratesFields({
  local,
  emit,
  disabled,
  setTouched,
  regions,
  zones,
  areas,
  regionsLoading,
  zonesLoading,
  areasLoading,
  emiratesLoading,
  regionError,
  zoneError,
  areaError,
  l,
  req,
  isRTL,
  locale,
  t,
}: DubaiNorthernEmiratesFieldsProps) {
  return (
    <div id="DubaiNorthEmirate" className="space-y-5 animate-in fade-in-50 duration-300">
      {/* Region select (depends on emirate) */}
      <div className="flex flex-col gap-2.5">
        <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
          <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {l.region}
          {req.region && <span className="text-destructive">*</span>}
        </label>
        <Select
          dir={isRTL ? 'rtl' : 'ltr'}
          disabled={
            disabled || local.emirateId === undefined || local.emirateId === null || emiratesLoading || regionsLoading
          }
          value={local.regionId !== undefined && local.regionId !== null ? String(local.regionId) : undefined}
          onValueChange={(v) => {
            const selectedRegion = regions.find((r) => String(getId(r)) === v);
            if (selectedRegion) {
              emit({
                regionId: getId(selectedRegion),
                regionManhalCode: getManhalCode(selectedRegion),
                zoneId: undefined,
                areaId: undefined,
                regionNameEn: getTitleEn(selectedRegion),
                regionNameAr: getTitleAr(selectedRegion),
                zoneManhalCode: null,
                zoneNameEn: null,
                zoneNameAr: null,
                areaNameEn: null,
                areaNameAr: null,
              });
            }
          }}
          onOpenChange={(o) => {
            if (!o) setTouched((t) => ({ ...t, regionId: true }));
          }}
        >
          <SelectTrigger
            className={cn(
              'h-12 rounded-xl border-2 bg-background shadow-sm transition-all duration-200',
              'hover:border-primary/50 hover:shadow-md focus:border-primary focus:ring-4 focus:ring-primary/10',
              'disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-60',
              regionsLoading && 'animate-pulse',
              regionError && 'border-destructive/50 focus:border-destructive focus:ring-destructive/10',
              isRTL ? 'text-right' : 'text-left'
            )}
          >
            <SelectValue placeholder={regionsLoading ? t.pickLocation.loading : l.region} />
          </SelectTrigger>
          <SelectContent className="rounded-xl border-2 shadow-lg" dir={isRTL ? 'rtl' : 'ltr'}>
            {regions.length === 0 && !regionsLoading && (
              <div className="p-4 text-sm text-muted-foreground text-center flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
                {t.pickLocation.noRegions}
              </div>
            )}
            {regions.map((r) => (
              <SelectItem
                key={getId(r)}
                value={String(getId(r))}
                className={cn(
                  'cursor-pointer hover:bg-primary/10 focus:bg-primary/10 rounded-lg my-0.5 transition-colors',
                  isRTL ? 'text-right' : 'text-left'
                )}
              >
                {locale === 'ar' ? getTitleAr(r) : getTitleEn(r)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {regionError && (
          <p className="text-xs text-destructive flex items-center gap-1.5 animate-in slide-in-from-top-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            {l.requiredField}
          </p>
        )}
      </div>

      {/* Zone select (depends on region) */}
      <div className="flex flex-col gap-2.5">
        <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
          <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {locale === 'ar'
            ? isDubaiOrAbuDhabi(local.emirateNameEn)
              ? l.zone
              : 'منطقة/قطاع'
            : isDubaiOrAbuDhabi(local.emirateNameEn)
              ? l.zone
              : 'District/Sector'}
          {req.zone && <span className="text-destructive">*</span>}
        </label>
        <Select
          dir={isRTL ? 'rtl' : 'ltr'}
          disabled={
            disabled || local.regionId === undefined || local.regionId === null || regionsLoading || zonesLoading
          }
          value={
            local.zoneManhalCode !== undefined && local.zoneManhalCode !== null
              ? local.zoneManhalCode
              : local.zoneId !== undefined && local.zoneId !== null
                ? String(local.zoneId)
                : undefined
          }
          onValueChange={(v) => {
            const selectedZone = zones.find((z) => getManhalCode(z) === v || String(getId(z)) === v);
            if (selectedZone) {
              emit({
                zoneId: getId(selectedZone),
                zoneManhalCode: getManhalCode(selectedZone),
                areaId: undefined,
                zoneNameEn: getTitleEn(selectedZone),
                zoneNameAr: getTitleAr(selectedZone),
                areaNameEn: null,
                areaNameAr: null,
              });
            }
          }}
          onOpenChange={(o) => {
            if (!o) setTouched((t) => ({ ...t, zoneId: true }));
          }}
        >
          <SelectTrigger
            className={cn(
              'h-12 rounded-xl border-2 bg-background shadow-sm transition-all duration-200',
              'hover:border-primary/50 hover:shadow-md focus:border-primary focus:ring-4 focus:ring-primary/10',
              'disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-60',
              zonesLoading && 'animate-pulse',
              zoneError && 'border-destructive/50 focus:border-destructive focus:ring-destructive/10',
              isRTL ? 'text-right' : 'text-left'
            )}
          >
            <SelectValue
              placeholder={
                zonesLoading
                  ? t.pickLocation.loading
                  : locale === 'ar'
                    ? isDubaiOrAbuDhabi(local.emirateNameEn)
                      ? l.zone
                      : 'منطقة/قطاع'
                    : isDubaiOrAbuDhabi(local.emirateNameEn)
                      ? l.zone
                      : 'District/Sector'
              }
            />
          </SelectTrigger>
          <SelectContent className="rounded-xl border-2 shadow-lg" dir={isRTL ? 'rtl' : 'ltr'}>
            {zones.length === 0 && !zonesLoading && (
              <div className="p-4 text-sm text-muted-foreground text-center flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
                {t.pickLocation.noZones}
              </div>
            )}
            {zones.map((z) => (
              <SelectItem
                key={getId(z)}
                value={getManhalCode(z) || String(getId(z))}
                className={cn(
                  'cursor-pointer hover:bg-primary/10 focus:bg-primary/10 rounded-lg my-0.5 transition-colors',
                  isRTL ? 'text-right' : 'text-left'
                )}
              >
                {locale === 'ar' ? getTitleAr(z) : getTitleEn(z)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {zoneError && (
          <p className="text-xs text-destructive flex items-center gap-1.5 animate-in slide-in-from-top-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            {l.requiredField}
          </p>
        )}
      </div>

      {/* Area select (depends on zone) */}
      <div className="flex flex-col gap-2.5">
        <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
          <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
          {locale === 'ar'
            ? isDubaiOrAbuDhabi(local.emirateNameEn)
              ? l.area
              : 'حوض/حي'
            : isDubaiOrAbuDhabi(local.emirateNameEn)
              ? l.area
              : 'Sector/Area'}
          {req.area && <span className="text-destructive">*</span>}
        </label>
        <Select
          dir={isRTL ? 'rtl' : 'ltr'}
          disabled={disabled || local.zoneId === undefined || local.zoneId === null || zonesLoading || areasLoading}
          value={
            local.areaManhalCode !== undefined && local.areaManhalCode !== null
              ? local.areaManhalCode
              : local.areaId !== undefined && local.areaId !== null
                ? String(local.areaId)
                : undefined
          }
          onValueChange={(v) => {
            const selectedArea = areas.find((a) => getManhalCode(a) === v || String(getId(a)) === v);
            if (selectedArea) {
              emit({
                areaId: getId(selectedArea),
                areaManhalCode: getManhalCode(selectedArea),
                areaNameEn: getTitleEn(selectedArea),
                areaNameAr: getTitleAr(selectedArea),
              });
            }
          }}
          onOpenChange={(o) => {
            if (!o) setTouched((t) => ({ ...t, areaId: true }));
          }}
        >
          <SelectTrigger
            className={cn(
              'h-12 rounded-xl border-2 bg-background shadow-sm transition-all duration-200',
              'hover:border-primary/50 hover:shadow-md focus:border-primary focus:ring-4 focus:ring-primary/10',
              'disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-60',
              areasLoading && 'animate-pulse',
              areaError && 'border-destructive/50 focus:border-destructive focus:ring-destructive/10',
              isRTL ? 'text-right' : 'text-left'
            )}
          >
            <SelectValue
              placeholder={
                areasLoading
                  ? t.pickLocation.loading
                  : locale === 'ar'
                    ? isDubaiOrAbuDhabi(local.emirateNameEn)
                      ? l.area
                      : 'حوض/حي'
                    : isDubaiOrAbuDhabi(local.emirateNameEn)
                      ? l.area
                      : 'Sector/Area'
              }
            />
          </SelectTrigger>
          <SelectContent className="rounded-xl border-2 shadow-lg" dir={isRTL ? 'rtl' : 'ltr'}>
            {areas.length === 0 && !areasLoading && (
              <div className="p-4 text-sm text-muted-foreground text-center flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
                {t.pickLocation.noAreas}
              </div>
            )}
            {areas.map((a) => (
              <SelectItem
                key={getId(a)}
                value={getManhalCode(a) || String(getId(a))}
                className={cn(
                  'cursor-pointer hover:bg-primary/10 focus:bg-primary/10 rounded-lg my-0.5 transition-colors',
                  isRTL ? 'text-right' : 'text-left'
                )}
              >
                {locale === 'ar' ? getTitleAr(a) : getTitleEn(a)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {areaError && (
          <p className="text-xs text-destructive flex items-center gap-1.5 animate-in slide-in-from-top-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            {l.requiredField}
          </p>
        )}
      </div>

      {/* Street Name (Northern Emirates only) */}
      <div className="flex flex-col gap-2.5">
        <label htmlFor="street-name" className="text-sm font-medium text-foreground flex items-center gap-1.5">
          <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {locale === 'ar' ? 'اسم الشارع' : 'Street Name'}
        </label>
        <DeferredInput
          id="street-name"
          type="text"
          placeholder={locale === 'ar' ? 'اسم الشارع' : 'Street Name'}
          value={local.streetName || ''}
          onCommit={(e) => emit({ streetName: e })}
          disabled={disabled}
          className="h-10 rounded-lg border-2 bg-white dark:bg-white text-foreground"
        />
      </div>

      {/* Street Number (Northern Emirates only) */}
      <div className="flex flex-col gap-2.5">
        <label htmlFor="street-number" className="text-sm font-medium text-foreground flex items-center gap-1.5">
          <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
            />
          </svg>
          {locale === 'ar' ? 'رقم الشارع' : 'Street Number'}
        </label>
        <DeferredInput
          id="street-number"
          type="text"
          placeholder={locale === 'ar' ? 'رقم الشارع' : 'Street Number'}
          value={local.mainPlotId || ''}
          onCommit={(e) => emit({ mainPlotId: e })}
          disabled={disabled}
          className="h-10 rounded-lg border-2 bg-white dark:bg-white text-foreground"
        />
      </div>
    </div>
  );
}

// ============================================
// Abu Dhabi & Dubai Emirate Layout (Map-driven for AD, Plot-driven for Dubai)
// ============================================
type AbuDhabiEmirateFieldsProps = {
  local: AddressValue;
  emit: (updates: Partial<AddressValue>) => void;
  disabled?: boolean;
  hasMapSelection: boolean;
  setIsMapDialogOpen: (open: boolean) => void;
  regions: Region[];
  zones: Zone[];
  abuDhabiAreas: Area[];
  regionsLoading: boolean;
  zonesLoading: boolean;
  abuDhabiAreasLoading: boolean;
  lockAbuDhabiFields: boolean;
  touched: { [K in keyof AddressValue]?: boolean };
  setTouched: React.Dispatch<React.SetStateAction<{ [K in keyof AddressValue]?: boolean }>>;
  regionPlaceholder: string;
  zonePlaceholder: string;
  areaPlaceholder: string;
  isRTL: boolean;
  locale: string;
  emirateManhalCode?: string | null;
  t: {
    pickLocation: {
      loading: string;
    };
  };
};

export function AbuDhabiEmirateFields({
  local,
  disabled,
  hasMapSelection,
  setIsMapDialogOpen,
  locale,
  emit,
  emirateManhalCode,
}: AbuDhabiEmirateFieldsProps) {
  // Determine emirate type based on manhal code
  const isAbuDhabi = emirateManhalCode === 'AE.001';
  const isDubai = emirateManhalCode === 'AE.002';
  const plotNumberLabel = isDubai
    ? locale === 'ar'
      ? 'أدخل رقم المكاني'
      : 'Enter Makani Number'
    : locale === 'ar'
      ? 'أدخل رقم Onwani'
      : 'Enter Onwani Number';
  const emirateDetailTitle = isDubai
    ? locale === 'ar'
      ? 'تفاصيل إمارة دبي'
      : 'Dubai Details'
    : locale === 'ar'
      ? 'تفاصيل إمارة أبوظبي'
      : 'Abu Dhabi Details';
  const plotNumberPlaceholder = isDubai
    ? locale === 'ar'
      ? 'مثال: 12345 67890'
      : 'Example: 12345 67890'
    : locale === 'ar'
      ? 'مثال: 123456789'
      : 'Example: 123456789';
  const [manualPlotNumber, setManualPlotNumber] = React.useState('');
  const [isLoadingPlot, setIsLoadingPlot] = React.useState(false);
  const [plotError, setPlotError] = React.useState<string | null>(null);

  const handleFetchPlotData = async () => {
    if (!manualPlotNumber.trim()) {
      const errorMsg =
        locale === 'ar'
          ? isDubai
            ? 'الرجاء إدخال رقم المكاني'
            : 'الرجاء إدخال رقم Onwani'
          : isDubai
            ? 'Please enter a Makani number'
            : 'Please enter an Onwani number';
      setPlotError(errorMsg);
      return;
    }

    if (!emirateManhalCode) {
      setPlotError(locale === 'ar' ? 'خطأ في الحصول على رمز الإمارة' : 'Error: emirate code not available');
      return;
    }

    setIsLoadingPlot(true);
    setPlotError(null);

    try {
      const ppBaseUrl = PP_BASE_URL;
      const url = `${ppBaseUrl}/reference/plots?filter=${encodeURIComponent(manualPlotNumber.trim())}&stateCode=${encodeURIComponent(emirateManhalCode)}`;
      const data = await authenticatedFetcher<{ data: any }>(url);

      if (data && data.data) {
        const plotData = data.data;

        // Construct full address strings (matching Onwani/Makani format)
        const addressEn = [plotData.areaNameEn, plotData.zoneNameEn, plotData.regionNameEn, plotData.emirateNameEn]
          .filter(Boolean)
          .join(', ');

        const addressAr = [plotData.areaNameAr, plotData.zoneNameAr, plotData.regionNameAr, plotData.emirateNameAr]
          .filter(Boolean)
          .join(', ');

        // Emit all enriched plot data matching map selection structure
        emit({
          emirateId: plotData.emirateId,
          emirateNameEn: plotData.emirateNameEn,
          emirateNameAr: plotData.emirateNameAr,
          emirateManhalCode: plotData.emirateManhalCode,
          regionId: plotData.regionId,
          regionNameEn: plotData.regionNameEn,
          regionNameAr: plotData.regionNameAr,
          regionManhalCode: plotData.regionManhalCode,
          zoneId: plotData.zoneId,
          zoneNameEn: plotData.zoneNameEn,
          zoneNameAr: plotData.zoneNameAr,
          zoneManhalCode: plotData.zoneManhalCode,
          areaId: plotData.areaId,
          areaNameEn: plotData.areaNameEn,
          areaNameAr: plotData.areaNameAr,
          areaManhalCode: plotData.areaManhalCode,
          fullAddressEn: addressEn,
          fullAddressAr: addressAr,
          plotId: manualPlotNumber.trim(),
          premisesPlotId: manualPlotNumber.trim(),
          mainPlotId: manualPlotNumber.trim(),
          houseNumber: plotData.plotNumber || plotData.buildingNumber,
          latitude: plotData.latitude ? Number(plotData.latitude) : null,
          longitude: plotData.longitude ? Number(plotData.longitude) : null,
          streetName: plotData.streetName,
        });
        setManualPlotNumber('');
        setPlotError(null);
      } else {
        setPlotError(locale === 'ar' ? 'لم يتم العثور على بيانات العنوان' : 'No address data returned');
      }
    } catch (error: any) {
      console.error('Plot fetch error:', error);
      const message =
        error?.status === 404
          ? locale === 'ar' ? 'لم يتم العثور على بيانات العنوان' : 'No address data returned'
          : locale === 'ar' ? 'تعذّر جلب بيانات قطعة الأرض.' : 'Unable to fetch plot data';
      setPlotError(message);
    } finally {
      setIsLoadingPlot(false);
    }
  };
  return (
    <div className="space-y-6 animate-in fade-in-50 duration-300">
      {/* Abu Dhabi/Dubai Section Header */}
      <div className="flex items-center gap-2 pt-4 pb-2 border-t border-border/50">
        <svg className="w-5 h-5 text-aegreen-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
          />
        </svg>
        <h4 className="text-sm font-semibold text-foreground">{emirateDetailTitle}</h4>
      </div>

      {/* Map Selection Card */}
      <div className="bg-gradient-to-br from-aegreen-50 to-blue-50 dark:from-aegreen-950/20 dark:to-blue-950/20 rounded-xl p-5 border-2 border-aegreen-200/50 dark:border-aegreen-800/50 shadow-sm">
        <div className="flex flex-col gap-4">
          {/* Manual Plot Number Entry */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground flex items-center gap-2">
              <svg className="w-4 h-4 text-aegreen-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
              {plotNumberLabel}
            </label>
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder={plotNumberPlaceholder}
                value={manualPlotNumber}
                onChange={(e) => {
                  setManualPlotNumber(e.target.value);
                  setPlotError(null);
                }}
                disabled={disabled || isLoadingPlot}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleFetchPlotData();
                  }
                }}
                className="flex-1 h-10 bg-white dark:bg-white text-foreground"
              />
              <Button
                type="button"
                onClick={handleFetchPlotData}
                disabled={disabled || isLoadingPlot || !manualPlotNumber.trim()}
                className="bg-aegreen-600 hover:bg-aegreen-700 text-white px-4 h-10 rounded-lg"
              >
                {isLoadingPlot ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                )}
              </Button>
            </div>
            {plotError && (
              <p className="text-xs text-destructive flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {plotError}
              </p>
            )}
          </div>

          {/* Divider - Only show for Abu Dhabi (since it has map option) */}
          {isAbuDhabi && (
            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-aegreen-200/50 dark:border-aegreen-800/50"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="px-2 bg-gradient-to-br from-aegreen-50 to-blue-50 dark:from-aegreen-950/20 dark:to-blue-950/20 text-aegreen-600 dark:text-aegreen-400 font-medium">
                  {locale === 'ar' ? 'أو' : 'OR'}
                </span>
              </div>
            </div>
          )}

          {/* Map Selection Button - Only for Abu Dhabi */}
          {isAbuDhabi && (
            <Button
              type="button"
              onClick={() => setIsMapDialogOpen(true)}
              className={cn(
                'h-14 w-full rounded-xl font-medium shadow-sm transition-all duration-200',
                hasMapSelection
                  ? 'bg-aegreen-600/20 text-aegreen-700 dark:text-aegreen-400 border-2 border-aegreen-600/50 hover:bg-aegreen-600/30 hover:border-aegreen-600/60'
                  : 'bg-aegreen-600 text-white hover:bg-aegreen-700',
                'active:scale-[0.98]',
                'focus:outline-none focus:ring-4 focus:ring-aegreen-500/30',
                'disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-60',
                'flex items-center justify-center gap-3'
              )}
              disabled={disabled}
            >
              {hasMapSelection ? (
                <>
                  <svg
                    className="w-6 h-6 animate-in zoom-in duration-200"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-base font-semibold">
                    {locale === 'ar' ? 'تم اختيار الموقع من الخريطة' : 'Location Selected from Map'}
                  </span>
                </>
              ) : (
                <>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                    />
                  </svg>
                  <span className="text-base font-semibold">
                    {locale === 'ar' ? 'اختر من الخريطة (Onwani)' : 'Select From Map (Onwani)'}
                  </span>
                </>
              )}
            </Button>
          )}

          {/* Info Text - Dynamic based on emirate */}
          {isAbuDhabi ? (
            <p className="text-center text-xs text-aegreen-700 dark:text-aegreen-400 flex items-center justify-center gap-1.5 font-medium">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {locale === 'ar'
                ? 'سيتم ملء جميع البيانات تلقائياً من الخريطة'
                : 'All address data will be automatically filled from the map'}
            </p>
          ) : (
            <p className="text-center text-xs text-aegreen-700 dark:text-aegreen-400 flex items-center justify-center gap-1.5 font-medium">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {locale === 'ar'
                ? 'سيتم ملء جميع البيانات تلقائياً من بحث المكاني'
                : 'All address data will be automatically filled from the Makani search'}
            </p>
          )}
        </div>
      </div>

      {/* Read-only Address Display (when map selection exists OR when plot data is fetched) */}
      {(hasMapSelection || local.fullAddressEn || local.fullAddressAr || local.plotId) &&
        (local.regionNameEn || local.zoneNameEn || local.areaNameEn || local.streetName || local.houseNumber) && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border-2 border-gray-200 dark:border-gray-700 shadow-sm animate-in slide-in-from-top-2 duration-300">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
              <svg className="w-5 h-5 text-aegreen-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <h5 className="text-sm font-bold text-foreground">
                {locale === 'ar' ? 'العنوان المحدد' : 'Selected Address'}
              </h5>
              <Badge
                variant="outline"
                className="ml-auto bg-aegreen-50 dark:bg-aegreen-950/30 text-aegreen-700 dark:text-aegreen-400 border-aegreen-300 dark:border-aegreen-700"
              >
                {hasMapSelection
                  ? locale === 'ar'
                    ? 'من الخريطة'
                    : 'From Map'
                  : locale === 'ar'
                    ? 'من البحث'
                    : 'From Search'}
              </Badge>
            </div>

            <div className="space-y-3">
              {/* Location Info - Compact Grid */}
              {local.emirateNameEn && (
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase mb-0.5">
                      {locale === 'ar' ? 'الإمارة' : 'EMIRATE'}
                    </p>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      {locale === 'ar' ? local.emirateNameAr || local.emirateNameEn : local.emirateNameEn}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase mb-0.5">
                      {locale === 'ar' ? 'المدينة' : 'CITY'}
                    </p>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      {locale === 'ar'
                        ? local.municipalityNameAr ||
                          local.municipalityNameEn ||
                          local.regionNameAr ||
                          local.regionNameEn
                        : local.municipalityNameEn || local.regionNameEn}
                    </p>
                  </div>
                  {local.regionNameEn && (
                    <div>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase mb-0.5">
                        {locale === 'ar' ? 'المنطقة' : 'REGION'}
                      </p>
                      <p className="font-semibold text-gray-900 dark:text-gray-100">
                        {locale === 'ar' ? local.regionNameAr || local.regionNameEn : local.regionNameEn}
                      </p>
                    </div>
                  )}
                  {local.areaNameEn && (
                    <div>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase mb-0.5">
                        {locale === 'ar' ? 'القطاع' : 'SECTOR'}
                      </p>
                      <p className="font-semibold text-gray-900 dark:text-gray-100">
                        {locale === 'ar' ? local.areaNameAr || local.areaNameEn : local.areaNameEn}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Onwani/Makani Plot Details Section */}
              <div className="bg-aegreen-50 dark:bg-aegreen-950/30 rounded-lg p-3 border border-aegreen-200 dark:border-aegreen-800">
                <p className="text-[10px] text-aegreen-700 dark:text-aegreen-400 font-semibold uppercase tracking-wide mb-2">
                  {isDubai
                    ? locale === 'ar'
                      ? 'معلومات المكاني'
                      : 'MAKANI INFORMATION'
                    : locale === 'ar'
                      ? 'معلومات القطعة'
                      : 'PLOT INFORMATION'}
                </p>

                <div className="grid grid-cols-2 gap-2 text-xs">
                  {(local.premisesPlotId || local.mainPlotId) && (
                    <div>
                      <p className="text-[10px] text-aegreen-700 dark:text-aegreen-400 uppercase tracking-wide mb-0.5">
                        {isDubai ? 'MAKANI ID' : 'PLOT ID'}
                      </p>
                      <p className="font-bold text-aegreen-900 dark:text-aegreen-200">
                        {local.premisesPlotId || local.mainPlotId}
                      </p>
                    </div>
                  )}
                  {local.houseNumber && (
                    <div>
                      <p className="text-[10px] text-aegreen-700 dark:text-aegreen-400 uppercase tracking-wide mb-0.5">
                        {isDubai
                          ? locale === 'ar'
                            ? 'رقم المكاني'
                            : 'MAKANI NUMBER'
                          : locale === 'ar'
                            ? 'رقم القطعة'
                            : 'PLOT NUMBER'}
                      </p>
                      <p className="font-semibold text-aegreen-900 dark:text-aegreen-200">{local.houseNumber}</p>
                    </div>
                  )}
                  {local.streetName && (
                    <div>
                      <p className="text-[10px] text-aegreen-700 dark:text-aegreen-400 uppercase tracking-wide mb-0.5">
                        {locale === 'ar' ? 'رقم الطريق' : 'ROAD ID'}
                      </p>
                      <p className="font-semibold text-aegreen-900 dark:text-aegreen-200">{local.streetName}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Coordinates Section */}
              {(local.latitude || local.longitude) && (
                <div className="bg-orange-50 dark:bg-orange-950/30 rounded-lg p-3 border border-orange-200 dark:border-orange-700">
                  <p className="text-[10px] text-orange-700 dark:text-orange-400 font-semibold uppercase tracking-wide mb-2">
                    {locale === 'ar' ? 'الإحداثيات' : 'COORDINATES'}
                  </p>

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {local.latitude && (
                      <div>
                        <p className="text-[10px] text-orange-700 dark:text-orange-400 uppercase tracking-wide mb-0.5">
                          {locale === 'ar' ? 'خط العرض' : 'LATITUDE'}
                        </p>
                        <p className="font-mono font-semibold text-orange-900 dark:text-orange-200 text-[10px]">
                          {Number(local.latitude).toFixed(8)}
                        </p>
                      </div>
                    )}
                    {local.longitude && (
                      <div>
                        <p className="text-[10px] text-orange-700 dark:text-orange-400 uppercase tracking-wide mb-0.5">
                          {locale === 'ar' ? 'خط الطول' : 'LONGITUDE'}
                        </p>
                        <p className="font-mono font-semibold text-orange-900 dark:text-orange-200 text-[10px]">
                          {Number(local.longitude).toFixed(8)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Address Section - Moved to bottom */}
              {(local.fullAddressEn || local.fullAddressAr) && (
                <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-3 border border-blue-200 dark:border-blue-800 space-y-1.5">
                  <p className="text-[10px] text-blue-600 dark:text-blue-400 font-semibold uppercase tracking-wide">
                    {locale === 'ar' ? 'العنوان (إنجليزي)' : 'ADDRESS (ENGLISH)'}
                  </p>
                  <p className="text-xs font-medium text-blue-900 dark:text-blue-100">{local.fullAddressEn || ''}</p>
                  {local.fullAddressAr && (
                    <>
                      <p className="text-[10px] text-blue-600 dark:text-blue-400 font-semibold uppercase tracking-wide mt-2">
                        {locale === 'ar' ? 'العنوان (عربي)' : 'ADDRESS (ARABIC)'}
                      </p>
                      <p className="text-xs font-medium text-blue-900 dark:text-blue-100" dir="rtl">
                        {local.fullAddressAr}
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* Address Type */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  {locale === 'ar' ? 'نوع العنوان:' : 'Address Type:'}
                </span>
                <span className="px-2 py-1 text-xs font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-md">
                  Plot
                </span>
              </div>
            </div>

            {/* Action hint */}
            <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-600 dark:text-gray-400 text-center flex items-center justify-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                {locale === 'ar'
                  ? 'لتعديل العنوان، اضغط على زر الخريطة أعلاه'
                  : 'To modify address, click the map button above'}
              </p>
            </div>
          </div>
        )}

      {/* Placeholder when no selection */}
      {!hasMapSelection && (
        <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-8 border-2 border-dashed border-gray-300 dark:border-gray-700 text-center">
          <svg
            className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
            />
          </svg>
          <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
            {locale === 'ar' ? 'لم يتم اختيار موقع بعد' : 'No Location Selected Yet'}
          </h6>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {isDubai
              ? locale === 'ar'
                ? 'أدخل رقم المكاني'
                : 'Enter a Makani number'
              : locale === 'ar'
                ? 'أدخل رقم Onwani أو اضغط على زر الخريطة لتحديد موقعك'
                : 'Enter an Onwani number or click the map button to select your location'}
          </p>
        </div>
      )}
    </div>
  );
}
